export interface SignalLifecycleFields {
  outcome?: string | null;
  pnl_dollars?: number | null;
}

export type SignalLifecycleState = "open" | "win" | "loss" | "breakeven";

function rootSymbol(symbol: string): string {
  return (symbol || "").replace(/[A-Z]\d+$/, "").toUpperCase();
}

export function formatPrice(price: number | null | undefined, symbol: string): string {
  if (price === null || price === undefined || !Number.isFinite(price)) return "—";

  switch (rootSymbol(symbol)) {
    case "HG":
      return price.toFixed(4);
    case "CL":
      return price.toFixed(2);
    case "GC":
      return price.toFixed(1);
    default:
      return price.toFixed(2);
  }
}

export function formatConfidencePercent(confidence: number | null | undefined): string {
  if (confidence === null || confidence === undefined || !Number.isFinite(confidence)) return "—";
  return `${Math.round(confidence * 100)}%`;
}

export function formatValueWithConfidence(value: string | null | undefined, confidence: number | null | undefined): string | undefined {
  if (!value) return undefined;
  return `${value} (${formatConfidencePercent(confidence)})`;
}

export function isSignalClosed(signal: SignalLifecycleFields): boolean {
  if (signal.pnl_dollars !== null && signal.pnl_dollars !== undefined && Number.isFinite(signal.pnl_dollars)) {
    return true;
  }
  return !!signal.outcome && signal.outcome.toUpperCase() !== "PENDING";
}

export function signalLifecycleState(signal: SignalLifecycleFields): SignalLifecycleState {
  if (!isSignalClosed(signal)) return "open";

  if (signal.pnl_dollars !== null && signal.pnl_dollars !== undefined && Number.isFinite(signal.pnl_dollars)) {
    if (signal.pnl_dollars > 0) return "win";
    if (signal.pnl_dollars < 0) return "loss";
    return "breakeven";
  }

  const outcome = signal.outcome?.toUpperCase();
  if (outcome === "WIN") return "win";
  if (outcome === "LOSS") return "loss";
  if (outcome === "BREAKEVEN" || outcome === "BE") return "breakeven";
  return "open";
}

export function signalResultIcon(state: SignalLifecycleState): string {
  if (state === "win") return "✓";
  if (state === "loss") return "✗";
  if (state === "breakeven") return "—";
  return "⏳";
}

export function signalResultColor(state: SignalLifecycleState): string {
  if (state === "win") return "#22c55e";
  if (state === "loss") return "#ef4444";
  if (state === "breakeven") return "#8888a0";
  return "#8888a0";
}

export function formatGainDollars(amount: number): string {
  return `+$${Math.abs(Math.round(amount)).toLocaleString()}`;
}

export function formatLossDollars(amount: number): string {
  return `-$${Math.abs(Math.round(amount)).toLocaleString()}`;
}
